// pages/mobile/pushSubscription.js
// Connects this phone to push notifications: subscribes through the service
// worker with the server's public key and saves the subscription at
// /api/mobile/push/ (backend/mobile/views.py). Safe to call on every app open.
import { useSyncExternalStore } from 'react';
import api from '../../authentication/axiosInstance';

const READY_TIMEOUT_MS = 10000;
let connectionState = 'unknown';
let syncSequence = 0;
const listeners = new Set();
const publish = (state) => {
  connectionState = state;
  listeners.forEach((listener) => listener());
};
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const usePushConnection = () => useSyncExternalStore(subscribe, () => connectionState);

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof window.Notification !== 'undefined';

export const urlBase64ToUint8Array = (value) => {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`;
  const raw = window.atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (buffer, publicKey) => {
  if (!buffer) return false;
  const current = new Uint8Array(buffer);
  const expected = urlBase64ToUint8Array(publicKey);
  return current.length === expected.length && current.every((byte, index) => byte === expected[index]);
};

const serviceWorkerReady = () =>
  Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('The notification service did not start.')), READY_TIMEOUT_MS)
    ),
  ]);

// Resolves to 'subscribed', 'unsupported', 'not_allowed' or 'not_configured'.
const connect = async () => {
  if (!pushSupported()) return 'unsupported';
  if (window.Notification.permission !== 'granted') return 'not_allowed';

  const { data } = await api.get('/mobile/push/');
  if (!data.enabled || !data.public_key) return 'not_configured';

  const registration = await serviceWorkerReady();
  let subscription = await registration.pushManager.getSubscription();
  // A subscription made with an older server key can no longer receive pushes.
  if (subscription && !sameKey(subscription.options?.applicationServerKey, data.public_key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post('/mobile/push/', { subscription: subscription.toJSON() });
  return 'subscribed';
};

export const ensurePushSubscription = async () => {
  const sequence = ++syncSequence;
  publish('checking');
  try {
    const state = await connect();
    if (sequence === syncSequence) publish(state);
    return state;
  } catch (error) {
    if (sequence === syncSequence) publish('failed');
    throw error;
  }
};

export const sendTestNotification = () => api.post('/mobile/push/test/').then((response) => response.data);

// Recheck on return to the app as well as when the browser renews it.
export const watchPushSubscription = () => {
  const sync = () => ensurePushSubscription().catch((error) => console.error('Push setup failed:', error));
  const refreshVisible = () => {
    if (document.visibilityState !== 'hidden' && navigator.onLine !== false) sync();
  };
  sync();
  const onMessage = (event) => {
    if (event.data?.type === 'push-subscription-changed') sync();
  };
  const serviceWorker = navigator.serviceWorker;
  serviceWorker?.addEventListener('message', onMessage);
  window.addEventListener('focus', refreshVisible);
  window.addEventListener('pageshow', refreshVisible);
  window.addEventListener('online', refreshVisible);
  document.addEventListener('visibilitychange', refreshVisible);
  return () => {
    serviceWorker?.removeEventListener('message', onMessage);
    window.removeEventListener('focus', refreshVisible);
    window.removeEventListener('pageshow', refreshVisible);
    window.removeEventListener('online', refreshVisible);
    document.removeEventListener('visibilitychange', refreshVisible);
    syncSequence += 1;
    publish('unknown');
  };
};
